import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import moment from 'moment';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Card, 
  CardContent, 
  CardActionArea,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  TableSortLabel,
  styled,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const DashboardCard = ({ title, count, onClick, backgroundColor, icon: Icon }) => (
  <Grid item xs={12} sm={6}>
    <Card 
      sx={{ 
        backgroundColor, 
        boxShadow: '0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)',
        '&:hover': {
          boxShadow: '0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22)',
        },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <CardActionArea onClick={onClick}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <Box>
            <Typography variant="h6" component="div" sx={{ color: 'rgba(255, 255, 255, 0.85)', fontWeight: 'medium' }}>
              {title}
            </Typography>
            <Typography variant="h3" component="div" sx={{ color: 'white', mt: 1 }}>
              {count}
            </Typography>
          </Box>
        </CardContent>
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            left: '-10%',
            opacity: 0.2,
            transform: 'rotate(-10deg)',
            zIndex: 0,
          }}
        >
          <Icon sx={{ fontSize: 180, color: 'white' }} />
        </Box>
      </CardActionArea>
    </Card>
  </Grid>
);

const TypeSpan = styled('span')(({ theme, type }) => ({
  padding: '4px 8px',
  borderRadius: '4px',
  textTransform: 'uppercase',
  fontWeight: 'bold',
  fontSize: '0.75rem',
  backgroundColor: 
    type === 'USED' ? theme.palette.error.light :
    type === 'RETRIEVED' ? theme.palette.success.light :
    'transparent',
  color: 
    type === 'USED' || type === 'RETRIEVED' ? theme.palette.common.white :
    theme.palette.text.primary,
}));

const AdminPanel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('created');
  const [order, setOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [totalClients, setTotalClients] = useState(0);
  const [superResellersCount, setSuperResellersCount] = useState(0);
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });

  useEffect(() => {
    fetchTransactions();
    fetchSuperResellersData();
    fetchStatsTotals();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await api.get('/admin/transactions');
      setTransactions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setLoading(false);
    }
  };

  const fetchSuperResellersData = async () => {
    try {
      const response = await api.get('/admin/list');
      setTotalClients(response.data.totalClients);
      setSuperResellersCount(response.data.count);
    } catch (error) {
      console.error('Error fetching super resellers data:', error);
    }
  };

  const fetchStatsTotals = async () => {
    try {
      const response = await api.get('/admin/statstotals');
      const data = response.data;
      
      setChartData({
        labels: data.onDate,
        datasets: [
          {
            label: 'Number of Clients',
            data: data.clients,
            fill: false,
            borderColor: 'rgb(75, 192, 192)',
            tension: 0.1
          }
        ]
      });
    } catch (error) {
      console.error('Error fetching stats totals:', error);
    }
  };

  const handleCardClick = (filter) => {
    navigate(`/admin/clients?filter=${filter}`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredTransactions = transactions.filter(transaction =>
    Object.values(transaction).some(value =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const sortedTransactions = filteredTransactions.sort((a, b) => {
    if (b[orderBy] < a[orderBy]) {
      return order === 'asc' ? 1 : -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return order === 'asc' ? -1 : 1;
    }
    return 0;
  });

  const headCells = [
    { id: 'created', label: 'Date' },
    { id: 'type', label: 'Type' },
    { id: 'credits', label: 'Credits', numeric: true },
    { id: 'login', label: 'Login' },
    { id: 'mac', label: 'MAC' },
    { id: 'description', label: 'Description' },
    { id: 'expiry', label: 'Expiry' },
  ];

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Number of Clients Over Time',
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Number of Clients'
        },
        beginAtZero: true
      }
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <DashboardCard 
          title="All Clients" 
          count={totalClients} 
          onClick={() => handleCardClick('all')} 
          backgroundColor="rgb(75,119,190)"
          icon={PeopleIcon}
        />
        <DashboardCard 
          title="Super Resellers" 
          count={superResellersCount} 
          onClick={() => navigate('/super-resellers')} 
          backgroundColor="rgb(38,194,129)"
          icon={SupervisorAccountIcon}
        />
      </Grid>
      
      {/* Add the chart */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Client Growth
        </Typography>
        <Box sx={{ height: 400 }}>
          <Line options={chartOptions} data={chartData} />
        </Box>
      </Paper>
      
      {/* Keep the existing transactions table */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h5">
            Latest Transactions
          </Typography>
          <TextField
            label="Search Transactions"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: 300 }}
          />
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="transactions table">
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : sortedTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No transactions found
                  </TableCell>
                </TableRow>
              ) : (
                sortedTransactions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((transaction, index) => (
                    <TableRow key={index}>
                      <TableCell>{moment(transaction.created).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
                      <TableCell>
                        <TypeSpan type={transaction.type}>
                          {transaction.type}
                        </TypeSpan>
                      </TableCell>
                      <TableCell align="right">{transaction.credits}</TableCell>
                      <TableCell>{transaction.login}</TableCell>
                      <TableCell>{transaction.mac}</TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell>{moment(transaction.expiry).format('YYYY-MM-DD')}</TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredTransactions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default AdminPanel;
